import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { FilesAPI } from "./files.api";
import {
  GetFileLogsQuery,
  GetFilePathQuery,
  GetFilesQuery,
  UseCreateCaseFile,
  UseDeleteCaseFile,
  UseGetFileLogs,
  UseGetFilePath,
  UseGetFiles,
  UseGetInfiniteFiles,
  UseUpdateCaseFile,
} from "./files.types";
import { useAuth } from "@/contexts/AuthContext";
import { queryClient } from "@/index";

export const FilesQueryKeys = {
  all: ["files", "list"],
  list: (q: GetFilesQuery) => ["files", "list", q],
  filePath: (q: GetFilePathQuery) => ["files", "path", q],
  fileLogs: (q: GetFileLogsQuery) => ["files", "logs", q],
};

// The list, get-one and delete hooks take the routes as an `api` option
// (default FilesAPI) instead of reading CaseFilesContext, which imports this
// module. Callers inside Files pass `useCaseFiles().api`.
export const useGetFiles: UseGetFiles = (
  q,
  { api = FilesAPI, ...options } = {}
) => {
  return useQuery({
    queryKey: FilesQueryKeys.list(q),
    queryFn: () => api.get(q),
    ...options,
  });
};

export const useGetFilePath: UseGetFilePath = (q, options) => {
  return useQuery({
    queryKey: FilesQueryKeys.filePath(q),
    queryFn: () => FilesAPI.getFilePath(q),
    placeholderData: (data) => data,
    ...options,
  });
};

export const useGetInfiniteFiles: UseGetInfiniteFiles = (
  q,
  { api = FilesAPI, ...options } = {}
) => {
  return useInfiniteQuery({
    queryKey: FilesQueryKeys.list(q),
    queryFn: ({ pageParam }) =>
      api.get({
        ...q,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage?.page) return null;
      if (firstPage?.page - 1 === 0) return null;
      return firstPage?.page - 1;
    },
    initialPageParam: 1,
    placeholderData: (data) => data,
    ...options,
  });
};

export const useCreateCaseFile: UseCreateCaseFile = (options) => {
  return useMutation({
    mutationFn: FilesAPI.create,
    ...options,
  });
};

export const useUpdateCaseFile: UseUpdateCaseFile = (options) => {
  return useMutation({
    mutationFn: FilesAPI.update,
    ...options,
  });
};

export const useDeleteCaseFile: UseDeleteCaseFile = ({
  api = FilesAPI,
  ...options
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.delete,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: FilesQueryKeys.all });
    },
    ...options,
  });
};

export const useGetFileLogs: UseGetFileLogs = (q, options) => {
  return useQuery({
    queryKey: FilesQueryKeys.fileLogs(q),
    queryFn: () => FilesAPI.getLogs(q),
    ...options,
  });
};

export const refetchFiles = (q?: GetFilesQuery) => {
  if (q) {
    queryClient.invalidateQueries({ queryKey: FilesQueryKeys.list(q) });
  } else {
    queryClient.invalidateQueries({ queryKey: FilesQueryKeys.all });
  }
};
